import { AlertTriangle, Inbox, Loader2, WifiOff } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from './button';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';

export function Spinner({ className }: { className?: string }) {
  return <Loader2 aria-hidden className={cn('h-4 w-4 animate-spin text-mustard-600', className)} />;
}

export function ChargementBloc({ libelle = 'Chargement…' }: { libelle?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 px-4 py-12 text-sm text-ink-muted">
      <Spinner />
      {libelle}
    </div>
  );
}

export function Squelette({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded bg-surface-sunken', className)} />;
}

export function EtatVide({
  titre,
  description,
  action,
  icone,
}: {
  titre: string;
  description?: string;
  action?: ReactNode;
  icone?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center px-4 py-14 text-center">
      <div className="mb-3 rounded-full bg-surface-sunken p-3 text-ink-muted">
        {icone ?? <Inbox aria-hidden className="h-6 w-6" />}
      </div>
      <p className="text-sm font-medium text-ink">{titre}</p>
      {description ? <p className="mt-1 max-w-sm text-sm text-ink-muted">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function EtatErreur({
  erreur,
  onReessayer,
}: {
  erreur: unknown;
  onReessayer?: () => void;
}) {
  const message =
    erreur instanceof Error ? erreur.message : "Une erreur inattendue s'est produite.";

  /* Une coupure réseau n'est pas une panne de l'application, et la confondre
   * avec une erreur de serveur envoie chercher au mauvais endroit. Le code
   * vient d'`api-client`, qui distingue déjà les deux. */
  const reseau = erreur instanceof ApiError && erreur.code === 'RESEAU';

  return (
    <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
      <div
        className={cn(
          'mb-3 rounded-full p-3',
          reseau ? 'bg-surface-sunken text-ink-soft' : 'bg-danger-soft text-danger',
        )}
      >
        {reseau ? (
          <WifiOff aria-hidden className="h-6 w-6" />
        ) : (
          <AlertTriangle aria-hidden className="h-6 w-6" />
        )}
      </div>
      <p className="text-sm font-medium text-ink">
        {reseau ? 'Pas de connexion au serveur' : 'Impossible de charger ces données'}
      </p>
      <p className="mt-1 max-w-md text-sm text-ink-muted">{message}</p>
      {reseau ? (
        <p className="mt-1 max-w-md text-sm text-ink-muted">
          L’application est ouverte, mais ses données vivent sur le serveur : rien ne s’affichera
          tant qu’il ne répond pas.
        </p>
      ) : null}
      {onReessayer ? (
        <Button className="mt-4" variant="outline" onClick={onReessayer}>
          Réessayer
        </Button>
      ) : null}
    </div>
  );
}

/** Bandeau d'information contextuel, sobre et sans couleur superflue. */
export function Encart({
  ton = 'neutre',
  titre,
  children,
  className,
}: {
  ton?: 'neutre' | 'moutarde' | 'danger' | 'succes';
  titre?: string;
  children: ReactNode;
  className?: string;
}) {
  const tons = {
    neutre: 'border-line bg-surface-sunken text-ink-soft',
    moutarde: 'border-mustard-300 bg-mustard-50 text-ink',
    danger: 'border-danger/30 bg-danger-soft text-danger',
    succes: 'border-success/30 bg-success-soft text-success',
  } as const;

  return (
    <div className={cn('rounded-md border px-3 py-2 text-sm', tons[ton], className)}>
      {titre ? <p className="mb-0.5 font-medium">{titre}</p> : null}
      {children}
    </div>
  );
}
